import Axios from 'axios'
import humps from 'humps'
import TokenUtil from '../util/TokenUtil'
import AccessUrl from '@/util/AccessUrl'
import ModeUtil from '@/util/ModeUtil'

// トークンkey値の設定
const OLPHEN_TOKEN_KEY = 'x-olphen-token'
const OLPHEN_LOCALE_KEY = 'x-olphen-locale'
const DEFAULT_CONTENT_TYPE = 'application/json'
const REQUEST_TIME_OUT = 7200000

export default class ApiClient {
  constructor () {
    this.client = Axios.create({
      baseURL: process.env.VUE_APP_OPEN_API_HOST,
      timeout: REQUEST_TIME_OUT
    })

    /**
     * Snakelize Request
     */
    this.client.interceptors.request.use(request => {
      if (request.headers['Content-Type'] === 'application/json') {
        request.data = humps.decamelizeKeys(request.data)
      }
      return Promise.resolve(request)
    })

    /**
     * Camelize Response
     */
    this.client.interceptors.response.use(
      response => {
        const contentType = response.headers['Content-Type']
          ? response.headers['Content-Type']
          : response.headers['content-type']
        if (contentType && (contentType.indexOf('application/json') >= 0 || contentType.indexOf('application/json') >= 0)) {
          response.data = humps.camelizeKeys(response.data)
        }
        return Promise.resolve(response)
      },
      error => {
        const contentType = error.response.headers['Content-Type']
          ? error.response.headers['Content-Type']
          : error.response.headers['content-type']
        if (contentType && (contentType.indexOf('application/json') >= 0 || contentType.indexOf('application/json') >= 0)) {
          error.response.data = humps.camelizeKeys(error.response.data)
          const camelizeErrors = []
          for (const e of error.response.data.result.errors) {
            camelizeErrors.push({ resource: humps.camelize(e.resource), field: humps.camelize(e.field), code: e.code })
          }
          error.response.data.result.errors = camelizeErrors
        }
        return Promise.reject(error)
      }
    )

    /**
     * handle data property
     */
    this.client.interceptors.response.use(
      response => {
        return Promise.resolve(response.data)
      },
      error => {
        if (!error || !error.response || !error.response.status) {
          // レスポンスに問題がある場合も502ページ
          if (!process.env.VUE_APP_DEBUG_MODE) location.href = '/bad-gateway'
          return Promise.reject(error.response.data)
        }
        switch (error.response.status) {
          case 400:
            // 400時はエラー内容を返す
            return Promise.reject(error.response.data)
          case 401: {
            // 401時はエラーを返す
            return Promise.reject(error)
          }
          case 403:
            // 403時はいかなる場合も403ページ
            location.href = '/forbidden'
            return Promise.reject(error.response.data)
          case 404:
            // 404時はいかなる場合も404ページ
            if (!process.env.VUE_APP_DEBUG_MODE) location.href = '/not-found'
            return Promise.reject(error.response.data)
          case 500:
            // 500時はいかなる場合も500ページ
            if (!process.env.VUE_APP_DEBUG_MODE) location.href = '/internal-server-error'
            return Promise.reject(error.response.data)
          case 502:
            // 502時はいかなる場合も502ページ
            if (!process.env.VUE_APP_DEBUG_MODE) location.href = '/bad-gateway'
            return Promise.reject(error.response.data)
          case 503:
            // 503時はいかなる場合も503ページ
            if (!process.env.VUE_APP_DEBUG_MODE) location.href = '/maintenance'
            return Promise.reject(error.response.data)
          default:
            return Promise.reject(error.response.data)
        }
      }
    )
  }

  /**
   * トークンをヘッダーにマージする
   * @param {*} header
   */
  addTokenToHeader (config) {
    const token = TokenUtil.get()
    const defaultHeader = {
      'Content-Type': DEFAULT_CONTENT_TYPE,
      [OLPHEN_TOKEN_KEY]: token,
      [OLPHEN_LOCALE_KEY]: navigator.language.slice(0, 2)
    }
    if (!config) {
      // なにもない場合はデフォルトの値を返却
      return { headers: defaultHeader }
    } else if (config.headers) {
      // header指定がある場合は指定を優先してマージ
      config.headers = { ...defaultHeader, ...config.headers }
    } else {
      config.headers = defaultHeader
    }
    return config
  }

  /**
   * クエリパラメータをconfigにマージ
   */
  addQueryParams (config, params = {}) {
    const queryParams = {}
    const decamelizeParams = humps.decamelizeKeys(params)
    for (const key in decamelizeParams) {
      if (decamelizeParams[key] !== null && decamelizeParams[key] !== '') {
        queryParams[key] = decamelizeParams[key]
      }
    }
    if (!config) {
      return { params: queryParams }
    } else {
      config.params = queryParams
      return config
    }
  }

  get (apiPath, body = null, config = null) {
    config = this.addQueryParams(config, body)
    return this.client.get(apiPath, this.addTokenToHeader(config))
  }

  post (apiPath, body = null, config = null) {
    return this.client.post(apiPath, body, this.addTokenToHeader(config))
  }

  patch (apiPath, body = null, config = null) {
    return this.client.patch(apiPath, body, this.addTokenToHeader(config))
  }

  put (apiPath, body = null, config = null) {
    return this.client.put(apiPath, body, this.addTokenToHeader(config))
  }

  delete (apiPath, config = null) {
    return this.client.delete(apiPath, this.addTokenToHeader(config))
  }
}
